<template>
  <div
    ref="main"
    class="boost-filter"
    :class="{ 'is-sticky': isSticky }"
  >
    <div class="boost-filter__inner">
      <div
        v-if="$variable('page.type') === 'search'"
        class="boost-filter__tabs"
      >
        <button
          class="boost-filter__tab text-heading-2xs text-heading-xs-desktop"
          :class="{ [$class('active')]: tab === 'products' }"
          type="button"
          @click="handleTabClick('products')"
          v-html="tabText.products"
        />

        <button
          class="boost-filter__tab text-heading-2xs text-heading-xs-desktop"
          :class="{ [$class('active')]: tab === 'collections' }"
          type="button"
          @click="handleTabClick('collections')"
          v-html="tabText.collections"
        />

        <button
          class="boost-filter__tab text-heading-2xs text-heading-xs-desktop"
          :class="{ [$class('active')]: tab === 'pages' }"
          type="button"
          @click="handleTabClick('pages')"
          v-html="tabText.pages"
        />
      </div>

      <template v-if="tab === 'products'">
        <button
          class="
            boost-filter__filter-button
            text-heading-3xs
            text-heading-xs-desktop
          "
          type="button"
          @click="handleOpenDrawer"
        >
          <icon-filter aria-hidden="true" />
          <span v-text="$string('collection.filter')" />
        </button>

        <div class="boost-filter__sort-by">
          <div
            class="
              boost-filter__sort-by-button
              text-heading-3xs
              text-heading-xs-desktop
            "
          >
            <icon-chevron-down />

            <span
              class="boost-filter__sort-by-label"
              v-text="sortByLabel"
            />
          </div>

          <select
            id="sort-by"
            ref="sortBy"
            class="boost-filter__sort-by-select"
            name="sort-by"
            :title="$string('collection.sort_by')"
            @change="handleSortByChange"
          >
            <option
              v-for="sort of getActiveOptions"
              :id="sort.id"
              :key="sort.id"
              :value="sort.id"
              :selected="sortOption.id === sort.id"
              js-boost-filter="sortByOption"
              v-text="sort.label"
            />
          </select>
        </div>

        <div
          v-if="$variable('page.type') !== 'search'"
          class="boost-filter__facets-wrap"
        >
          <div
            class="boost-filter__item-count text-label-s"
            v-html="itemCountLabel"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Boost filter (main-collection)
 * -----------------------------------------------------------------------------
 * Collection and search filters.
 *
 * @param {Boolean} boostInitial - Boost initial load.
 * @param {Boolean} boostReady - Boost is ready.
 * @param {String} choices - Available choices.
 * @param {Object} sortByOptions - Options for sorting.
 * @param {String} tab - Current tab.
 * @param {Object} total - Total number of products/collections/pages.
 *
 */
import { mapActions, mapState } from 'vuex'

import IconChevronDown from '~icons/direction/chevron/down.svg'
import IconFilter from '~icons/misc/filter.svg'

export default {
  name: 'BoostFilter',

  components: {
    IconChevronDown,
    IconFilter,
  },

  props: {
    boostInitial: {
      type: Boolean,
      default: false,
    },
    boostReady: {
      type: Boolean,
      default: false,
    },
    choices: {
      type: String,
      default: '',
    },
    sortByOptions: {
      type: Object,
      default: () => ({}),
    },
    tab: {
      type: String,
      default: 'products',
    },
    total: {
      type: Object,
      default: () => ({
        collections: 0,
        pages: 0,
        products: 0,
      }),
    },
  },

  emits: {
    'choices-updated': (payload) => typeof payload === 'object',
    'clear-choices': () => true,
    'facets-ready': () => true,
    'update-tab': (payload) => typeof payload === 'string',
  },

  data() {
    return {
      choicesArray: [],
      dataChoices: '',
      hasNonPageValue: false,
      sortOption: {
        id: '',
        label: '',
      },
      isSticky: false,
      mainElementScrollTop: 0,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      scroll: (state) => state.index.scroll,
    }),

    /**
     * Compute active options by filtering 'sort by' props based on those
     * enabled in section settings.
     * @returns {Object}
     */
    getActiveOptions() {
      return Object.values(this.sortByOptions).filter((object) => {
        return object.value === true
      })
    },

    /**
     * Compute item count label.
     * @returns {String}
     */
    itemCountLabel() {
      const count = this.boostReady
        ? this.total.products
        : '<span></span>'

      return this.$string('collection.items', {
        pluralise: this.total.products,
        replace: { count },
      })
    },

    /**
     * Compute sort by label.
     * @returns {String}
     */
    sortByLabel() {
      return this.dataChoices.includes('sort')
        ? `${this.$string('collection.sort_by')} ${this.sortOption.label}`
        : this.$string('collection.sort_by')
    },

    /**
     * Compute tab text.
     * @returns {Object}
     */
    tabText() {
      const data = {};

      ['collections', 'pages', 'products'].forEach((type) => {
        const count = this.boostInitial
          ? '<span></span>'
          : this.total[type]

        data[type] = this.$string(`search.tabs.${type}`, { replace: { count } })
      })

      return data
    },
  },

  watch: {

    /**
     * Watch choices to update array.
     */
    choices(choices) {
      this.dataChoices = choices
      this.updateChoicesArray(choices)

      /**
       * Reset sort by select value when not sorting.
       */
      if (choices.includes('&sort=')) {
        return
      }

      if (this.$refs.sortBy) {
        this.$refs.sortBy.selectedIndex = 0
      }
    },

    /**
     * Watch scroll to made filter bar sticky.
     */
    scroll() {
      this.handleScroll()
    },
  },

  mounted() {
    this.mainElementScrollTop = this.$refs.main.offsetTop
    this.handleScroll()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
    }),

    /**
     * Compute animation delay.
     * @param {Number} index - Index of element in loop.
     * @returns {String}
     */
    animationDelay(index) {
      return this.$timing('quick', { index, unit: 's' })
    },

    /**
     * Open drawer on filter button click.
     */
    handleOpenDrawer() {
      if (!this.boostReady) {
        return
      }

      this.openOverlay({
        component: 'boost-drawer',
        ignoreDismissed: true,
        namespace: 'boostDrawer',
      })
    },

    /**
     * Receive user input from facets and pass up to main collection.
     * @param {String} data - Formatted update to user choices.
     */
    choicesUpdated(data) {
      this.$emit('choices-updated', data)
    },

    /**
     * Notify when 'clear all' button is clicked.
     */
    clearChoices() {
      this.$emit('clear-choices')
      this.hasNonPageValue = false

      this.sortOption = {
        id: '',
        label: '',
      }
    },

    /**
     * Update choices array passed to active values component on choices change.
     * @param {String} data - Choices string to split into an array.
     */
    updateChoicesArray(data) {
      this.choicesArray = data.split('&').filter((choice) => {
        return choice !== ''
      })

      if (!this.choicesArray.length) {
        this.hasNonPageValue = false

        this.sortOption = {
          id: '',
          label: '',
        }

        return
      }

      this.choicesArray.forEach((choice) => {
        if (!choice.includes('page')) {
          this.hasNonPageValue = true
        }

        if (choice.includes('sort')) {
          const value = choice.split('=')[1]

          const option = Object.values(this.sortByOptions).find((object) => {
            return object.id === value
          })

          const { id, label } = option

          this.sortOption = {
            id,
            label,
          }
        }
      })
    },

    /**
     * Update sort options on select change.
     * @param {Event} event - Change event.
     */
    handleSortByChange(event) {
      if (!this.boostReady) {
        return
      }

      const nativeOptions = document.querySelectorAll('[js-boost-filter="sortByOption"]')
      let matchValue = event.currentTarget.value
      let action = 'add'

      if (event.currentTarget.classList.contains('boost-filter__sort-by-desktop--option')) {
        matchValue = event.currentTarget.id

        if (event.currentTarget.classList.contains(this.$class('active'))) {
          event.currentTarget.classList.remove(this.$class('active'))
          action = 'remove'

          nativeOptions.forEach((option) => {
            option.removeAttribute('selected')
          })

        } else {
          event.currentTarget.classList.add(this.$class('active'))

          nativeOptions.forEach((option) => {
            if (option.id === matchValue) {
              option.setAttribute('selected', 'selected')

            } else {
              option.removeAttribute('selected')
            }
          })
        }
      }

      const option = Object.values(this.sortByOptions).find((object) => {
        return object.id === matchValue
      })

      const { id, label } = option

      const data = {
        requestParam: `&sort=${id}`,
        action,
      }

      this.$emit('choices-updated', data)

      this.sortOption = {
        id,
        label,
      }
    },

    /**
     * Return empty or full circle icon based on active status of option.
     * @param {String} sortId - Id of sort option.
     */
    getSortIcon(sortId) {
      if (this.dataChoices.includes(sortId)) {
        return 'icon-circle-full'
      }

      return 'icon-circle-empty'
    },

    /**
     * Handle tab click.
     * @param {String} type - Type to update tab to.
     */
    handleTabClick(type) {
      this.$emit('update-tab', type)
    },

    /**
     * Handle scroll for filter bar.
     */
    handleScroll() {
      const mainElement = this.$refs.main
      const headerHeight = document.getElementById('site-header-container').offsetHeight

      if (this.scroll.direction === 'down') {
        this.isSticky = this.scroll.y >= this.mainElementScrollTop
        mainElement.style.top = '-1px'
        mainElement.classList.remove('up')
        mainElement.classList.add('down')

        return
      }

      if (this.scroll.direction === 'up' && this.scroll.y >= (this.mainElementScrollTop - headerHeight)) {
        this.isSticky = true
        mainElement.style.top = `${headerHeight - 1}px`
        mainElement.classList.remove('down')
        mainElement.classList.add('up')
      } else {
        this.isSticky = false
        mainElement.style.top = 0
      }
    },
  },
}
</script>

<style lang="scss">
@import './boost-filter';
</style>
